import React, { useState, useEffect, useMemo } from 'react';
import { Card, Checkbox, Row, Col, Typography, Space } from 'antd';
import { SettingOutlined } from '@ant-design/icons';

const { Text } = Typography;

const useColumnVisibility = ({ 
    baseColumns, 
    storageKey, 
    defaultVisible = {}
}) => {
    // Get default visibility - all columns visible by default except actions which is always visible
    const getDefaultVisibility = () => {
        if (!baseColumns || baseColumns.length === 0) return {};
        
        const visibility = {};
        baseColumns.forEach(col => {
            if (col.key === 'actions') {
                visibility[col.key] = true; // Always visible
            } else {
                // Use provided default or default to true
                visibility[col.key] = defaultVisible[col.key] !== undefined 
                    ? defaultVisible[col.key] 
                    : true;
            }
        });
        return visibility;
    };

    const [columnVisibility, setColumnVisibility] = useState(() => {
        if (!baseColumns || baseColumns.length === 0) return {};
        
        if (!storageKey) {
            return getDefaultVisibility();
        }
        
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                const defaults = getDefaultVisibility();
                return { ...defaults, ...parsed, actions: true }; // Always keep actions visible
            } catch (e) {
                return getDefaultVisibility();
            }
        }
        return getDefaultVisibility();
    });

    // State for Actions column fixed position (default: false)
    const getActionsFixedState = () => {
        if (!storageKey) return false;
        const saved = localStorage.getItem(`${storageKey}_actionsFixed`);
        if (saved !== null) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                return false;
            }
        }
        return false; // Default to not fixed
    };

    const [actionsFixed, setActionsFixed] = useState(getActionsFixedState);

    // Update visibility when baseColumns change (if they weren't available initially)
    useEffect(() => {
        if (baseColumns && baseColumns.length > 0 && Object.keys(columnVisibility).length === 0) {
            const defaults = getDefaultVisibility();
            setColumnVisibility(defaults);
        }
    }, [baseColumns]);

    const handleColumnVisibilityChange = (key, checked) => {
        const newVisibility = { ...columnVisibility, [key]: checked };
        setColumnVisibility(newVisibility);
        
        if (storageKey) {
            localStorage.setItem(storageKey, JSON.stringify(newVisibility));
        }
    };

    const handleActionsFixedChange = (checked) => {
        setActionsFixed(checked);
        if (storageKey) {
            localStorage.setItem(`${storageKey}_actionsFixed`, JSON.stringify(checked));
        }
    };

    // Filter columns based on visibility and apply Actions column fixed setting
    const visibleColumns = useMemo(() => {
        if (!baseColumns || baseColumns.length === 0) return [];
        
        return baseColumns
            .filter(col => {
                if (col.key === 'actions') return true; // Always show actions
                return columnVisibility[col.key] !== false;
            })
            .map(col => {
                // Apply fixed setting for Actions column based on user preference
                if (col.key === 'actions') {
                    return { 
                        ...col, 
                        fixed: actionsFixed ? 'right' : undefined 
                    };
                }
                return col;
            });
    }, [baseColumns, columnVisibility, actionsFixed]);

    // Check if Actions column exists
    const actionsColumn = baseColumns?.find(col => col.key === 'actions');

    const ColumnVisibilityUI = (
        <Card 
            className="form-section"
            style={{ 
                marginBottom: 24,
                borderRadius: '16px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                border: '1px solid #e8f4f8',
                transition: 'all 0.3s ease',
                overflow: 'hidden'
            }}
            bodyStyle={{ padding: '32px' }}
        >
            <div style={{ 
                borderBottom: '2px solid #fff7e6',
                paddingBottom: 20,
                marginBottom: 24,
                background: 'linear-gradient(135deg, #fff7e6 0%, #ffffff 100%)',
                margin: '-32px -32px 24px -32px',
                padding: '24px 32px'
            }}>
                <Space align="center" size="middle">
                    <div style={{
                        background: 'linear-gradient(135deg, #faad14 0%, #d48806 100%)',
                        padding: '12px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(250, 173, 20, 0.3)'
                    }}>
                        <SettingOutlined style={{ color: 'white', fontSize: '24px' }} />
                    </div>
                    <div>
                        <Typography.Title 
                            level={4} 
                            style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}
                        >
                            Show/Hide Columns
                        </Typography.Title>
                        <Text type="secondary" style={{ fontSize: '15px', color: '#595959', display: 'block', marginTop: 4 }}>
                            Customize which columns are visible in the table
                        </Text>
                    </div>
                </Space>
            </div>
            <Row gutter={[16, 12]}>
                {baseColumns && baseColumns
                    .filter(col => col.key !== 'actions') // Exclude actions from visibility toggle
                    .map((col) => (
                        <Col xs={12} sm={8} md={6} lg={4} key={col.key}>
                            <Checkbox
                                checked={columnVisibility[col.key] !== false}
                                onChange={(e) => handleColumnVisibilityChange(col.key, e.target.checked)}
                                style={{ 
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    padding: '4px 0'
                                }}
                            >
                                {col.title}
                            </Checkbox>
                        </Col>
                    ))}
            </Row>
            {actionsColumn && (
                <div style={{ 
                    marginTop: 20, 
                    paddingTop: 16, 
                    borderTop: '2px solid #f0f0f0',
                    background: '#fafafa',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #e8e8e8'
                }}>
                    <Checkbox
                        checked={actionsFixed}
                        onChange={(e) => handleActionsFixedChange(e.target.checked)}
                        style={{ fontSize: '14px', fontWeight: 500 }}
                    >
                        <strong>📌 Fix Actions Column to Right</strong>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4, fontWeight: 'normal' }}>
                            Keep the Actions column visible when scrolling horizontally
                        </Text>
                    </Checkbox>
                </div>
            )}
        </Card>
    );

    return {
        columnVisibility,
        visibleColumns,
        ColumnVisibilityUI
    };
};

export default useColumnVisibility;

